import { computed } from 'vue';
import { isDefined } from 'class-validator';
import { t } from '@nova/core';

/**
 * Composable for handling units (pallets, skids, etc.) validation and configuration
 * Shared between Helios (warehouse) and Luna (carrier) apps
 *
 * @param {Ref} settings - Warehouse settings object (computed or ref)
 * @param {Object} validator - Validator object with rules (optional, for validation rules)
 * @param {Ref} existingValue - Existing units value (optional, for edit scenarios)
 * @returns {Object} Handling units configuration and validation
 */
export default function useLimiter(settings, useFlagFn, validator = null, existingValue = null) {
  const handlingUnitsFlag = useFlagFn('enable-handling-units');
  const unitLimiterFlag = useFlagFn('enable-unit-limiter');

  const shouldShowHandlingUnits = computed(() => {
    // Show if setting is enabled OR if there's already a value set (including 0)
    return (
      (handlingUnitsFlag.value.enabled &&
        Boolean(settings?.value?.includeHandlingUnitsFieldOnAppointmentForms)) ||
      isDefined(existingValue?.value)
    );
  });

  const shouldFetchUnavailableDates = computed(() => {
    // Fetch if either handling units or unit limiter feature is enabled
    return shouldShowHandlingUnits.value || unitLimiterFlag.value.enabled;
  });

  const handlingUnitsLabel = computed(() => {
    return (
      settings?.value?.handlingUnitsLabel || t('unitLimiter.unitLimitConfigurator.handlingUnits')
    );
  });

  const handlingUnitsHelperText = computed(() => {
    return settings?.value?.handlingUnitsHelperText || '';
  });

  const isHandlingUnitsRequired = computed(() => {
    return settings?.value?.isHandlingUnitsRequired || false;
  });

  const handlingUnitsMin = computed(() => {
    return settings?.value?.handlingUnitsMinimumThreshold || 0;
  });

  const handlingUnitsMax = computed(() => {
    return settings?.value?.handlingUnitsMaximumThreshold || 0;
  });

  const handlingUnitsMinAttr = computed(() => {
    return handlingUnitsMin.value > 0 ? handlingUnitsMin.value : undefined;
  });

  const handlingUnitsMaxAttr = computed(() => {
    return handlingUnitsMax.value > 0 ? handlingUnitsMax.value : undefined;
  });

  const handlingUnitsRules = computed(() => {
    if (!validator) return [];

    const rules = [];

    if (isHandlingUnitsRequired.value) {
      rules.push(validator.rules.required(handlingUnitsLabel.value)[0]);
    }

    rules.push(...validator.rules.positiveInteger(t('global.errors.mustBeAValidNumber')));
    if (handlingUnitsMin.value > 0) {
      rules.push(
        v =>
          !v ||
          v >= handlingUnitsMin.value ||
          t('global.errors.mustBeNumberGreaterThan', { min: handlingUnitsMin.value })
      );
    }

    if (handlingUnitsMax.value > 0) {
      rules.push(
        v =>
          !v ||
          v <= handlingUnitsMax.value ||
          t('global.errors.mustBeNumberLessThan', { max: handlingUnitsMax.value })
      );
    }

    return rules;
  });

  // Legacy methods for backwards compatibility
  const areHandlingUnitsEnabled = settingsObj =>
    handlingUnitsFlag.value.enabled &&
    Boolean(settingsObj?.includeHandlingUnitsFieldOnAppointmentForms);

  const getHandlingUnitsLabel = settingsObj => {
    return settingsObj?.handlingUnitsLabel || t('settings.groups.handlingUnits');
  };

  /**
   * Validate units step - checks if units value meets all requirements
   * @param {number|null} unitsValue - Current units value
   * @returns {boolean} Whether the units step is valid
   */
  const validateUnitsStep = unitsValue => {
    // If handling units is required, check if it has a valid value
    if (isHandlingUnitsRequired.value) {
      if (!isDefined(unitsValue) || unitsValue === null || unitsValue === '') {
        return false;
      }
    }

    // Check min/max constraints if value is provided
    if (isDefined(unitsValue)) {
      if (handlingUnitsMin.value > 0 && unitsValue < handlingUnitsMin.value) {
        return false;
      }
      if (handlingUnitsMax.value > 0 && unitsValue > handlingUnitsMax.value) {
        return false;
      }
    }

    return true;
  };

  /**
   * Get units validation rules array
   * Can be used with any validation system that accepts rule functions
   * @returns {Array<Function>} Array of validation rule functions
   */
  const getUnitsValidationRules = () => {
    const rules = [];

    // Required validation
    if (isHandlingUnitsRequired.value) {
      rules.push(v => {
        return (isDefined(v) && v !== null && v !== '') || t('global.errors.thisFieldIsRequired');
      });
    }

    // Min validation
    if (handlingUnitsMin.value > 0) {
      rules.push(v => {
        return (
          !isDefined(v) ||
          v === null ||
          v === '' ||
          v >= handlingUnitsMin.value ||
          `Minimum is ${handlingUnitsMin.value}`
        );
      });
    }

    // Max validation
    if (handlingUnitsMax.value > 0) {
      rules.push(v => {
        return (
          !isDefined(v) ||
          v === null ||
          v === '' ||
          v <= handlingUnitsMax.value ||
          `Maximum is ${handlingUnitsMax.value}`
        );
      });
    }

    // Positive number validation
    rules.push(v => {
      return (
        !isDefined(v) ||
        v === null ||
        v === '' ||
        (Number.isInteger(Number(v)) && Number(v) > 0) ||
        t('global.errors.mustBeAPositiveInteger')
      );
    });

    return rules;
  };

  /**
   * Create a function to fetch unavailable dates from the unit limit validation API
   * @param {Object} services - Services object with unitLimit.validate method
   * @param {number} defaultDays - Default number of days to fetch (default: 5)
   * @returns {Function} Async function to fetch unavailable dates
   */
  const createFetchUnavailableDates = (services, defaultDays = 5) => {
    return async (params, dateRange = null) => {
      const { warehouseId, dockId, loadTypeId, units, timezone = 'utc' } = params;

      // Only fetch if handling units or unit limiter feature is enabled
      if (!shouldFetchUnavailableDates.value) {
        return [];
      }

      // When unit limiter is enabled, allow null units - let the endpoint validate
      // When only handling units is enabled, require units to be provided
      if (!warehouseId || !dockId || !loadTypeId) {
        return [];
      }

      if (!units && !unitLimiterFlag.value.enabled) {
        return [];
      }

      try {
        // Import DateTime dynamically to avoid requiring it as a dependency
        const { DateTime } = await import('luxon');

        // Use provided dateRange or calculate default range from today
        let start, end;
        if (dateRange) {
          start = dateRange.start;
          end = dateRange.end;
        } else {
          const now = DateTime.now().setZone(timezone).startOf('day');
          start = now.toISODate();
          end = now.plus({ days: defaultDays }).toISODate();
        }

        const requestData = {
          warehouseId,
          dockId,
          loadTypeId,
          start,
          end,
          requestedUnits: units
        };

        const response = await services.unitLimit.validate(requestData);

        // Filter for days where valid is false and map to {date, message}
        return response
          .filter(dayResult => !dayResult.valid)
          .map(dayResult => ({
            date: dayResult.date,
            message: dayResult.message
          }));
      } catch (error) {
        console.error('Error fetching unavailable dates:', error);
        return [];
      }
    };
  };

  return {
    // Computed properties
    shouldShowHandlingUnits,
    shouldFetchUnavailableDates,
    handlingUnitsLabel,
    handlingUnitsHelperText,
    isHandlingUnitsRequired,
    handlingUnitsMin,
    handlingUnitsMax,
    handlingUnitsMinAttr,
    handlingUnitsMaxAttr,
    handlingUnitsRules,
    // Validation methods
    validateUnitsStep,
    getUnitsValidationRules,
    // API interaction
    createFetchUnavailableDates,
    // Legacy methods
    areHandlingUnitsEnabled,
    getHandlingUnitsLabel
  };
}
